/* eslint-disable @typescript-eslint/no-unused-expressions */
import { ConfigResponse } from "@zoom/appssdk";

import Config from "@readai/common/config";
import { Client, MeetingPlatform } from "@readai/common/types";
import { snakeKeys } from "@readai/common/utils/formatUtils";

import { PageView, TrackerProvider, TrackerUser } from "./types";
import { isBotTraffic } from "./utils";
import { getPageTitle, getUserId } from "./utils";
import log from "../logger";

const GA_ADDRESS = "https://www.googletagmanager.com/gtag/js";

let isLoaded = false;

const loadScript = (src: string) => {
  if (isLoaded) return Promise.resolve();

  return new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.type = "text/javascript";
    s.async = true;
    s.src = src;
    s.onload = resolve;
    s.onerror = reject;

    const node = document.getElementsByTagName("script")[0];
    if (node.parentNode) {
      node.parentNode.insertBefore(s, node);
    } else {
      // Fallback if there isn't another script on the page.
      document.head.appendChild(s);
    }
  });
};

const logUrlToGoogle = (
  (window) =>
  (enabled: boolean, pageview: PageView = {}): void => {
    const title = pageview.page_title || getPageTitle();
    const location = pageview.page_location || (window.location.href as string);
    const path = pageview.page_path || window.location.pathname;

    if (!enabled || isBotTraffic(path)) {
      log.info("tracking [disabled] logUrlToGoogle:", title, location, path);
      return;
    }

    window.gtag &&
      window.gtag("event", "page_view", {
        page_title: title,
        page_location: location,
        page_path: path,
        user_id: getUserId(),
      });
  }
)(window);

class TrackerGoogleAnalytics implements TrackerProvider {
  private propertyId: string;
  private debug: boolean;
  private enabled: boolean = Config.tracking.ENABLED;

  constructor() {
    this.propertyId = "unknown";
    this.debug = false;
  }

  initialize({ propertyId, debug }: { propertyId: string; debug?: boolean }) {
    this.propertyId = propertyId;
    this.debug = debug ?? false;

    loadScript(`${GA_ADDRESS}?id=${this.propertyId}`)
      .then(() => (isLoaded = true))
      .catch((reason) => {
        log.debug("unable to load google analytics: ", reason);
      });
  }

  configureGtag() {
    window.dataLayer = window.dataLayer ?? [];
    function gtag(..._args: unknown[]) {
      if (window.dataLayer) {
        // eslint-disable-next-line prefer-rest-params
        window.dataLayer.push(arguments);
      }
    }
    window.gtag = gtag;
    gtag("js", new Date());

    const user_id = getUserId();

    // Turn automatic page tracking off since this is a SPA
    // https://developers.google.com/analytics/devguides/collection/gtagjs/pages
    gtag("config", this.propertyId, {
      user_id,
      send_page_view: false,
      debug: this.debug,
    });

    if (user_id) {
      gtag("set", "user_properties", {
        read_id: user_id,
      });
    }
  }

  destroyGtag() {
    window.gtag = null;
  }

  setMeetingPlatform(meetingPlatform: MeetingPlatform) {
    window.gtag &&
      window.gtag("set", "user_properties", {
        meeting_platform: meetingPlatform,
        user_id: getUserId(),
        read_id: getUserId(),
      });
  }

  setClient(client: Client) {
    window.gtag &&
      window.gtag("set", "user_properties", {
        client,
        user_id: getUserId(),
        read_id: getUserId(),
      });
  }

  event(action: string, params?: object) {
    if (!this.enabled) {
      log.info("tracking [disabled] event:", action, snakeKeys(params));
      return;
    }

    window.gtag &&
      window.gtag(
        "event",
        action,
        snakeKeys({ params, userId: getUserId(), readId: getUserId() }),
      );
  }

  pageviewEvent(pageview: PageView) {
    logUrlToGoogle(this.enabled, pageview);
  }

  enable() {
    // if we don't enable tracking in Config, don't allow it to be enabled
    if (!Config.tracking.ENABLED) return;
    this.configureGtag();
    this.enabled = true;
  }

  disable() {
    this.destroyGtag();
    this.enabled = false;
  }

  resetUser() {}
  setUser(
    _user: TrackerUser | null,
    _currentUser?: TrackerUser | null | undefined,
    _client?: Client | null | undefined,
  ) {}
  setRunningContext(_runningContext: string) {}
  setMeetingRole(_meetingRole: string) {}
  setZoomConfig(_zoomConfig: ConfigResponse) {}
  setPeopleProperties(_properties: object) {}
  register(_params: object) {}
  startSessionRecording() {}
  stopSessionRecording() {}
}

const trackerGoogleAnalytics = new TrackerGoogleAnalytics();

export default trackerGoogleAnalytics;
